// folder-loader.js — fetch + cache the per-pathway folder-content index that
// backs `content`-type drilldown in the flat (menu) pathways (Brief 168).
//
// The flat pathways' top-level rows live in `pathways/<id>/pathway.json`
// (loaded by pathway-loader.js). A `content`-type row is a drilldown FOLDER —
// iOS `TableListViewController.m -didSelectRowAtIndexPath:` pushes a fresh
// TableListViewController for its children. The children aren't inlined in
// pathway.json (180_days descends 5 levels to ~12k leaves); they live in a
// single lazily-fetched `pathways/<id>/folders.json` produced by
// tools/build-folders.mjs, mapping every reachable folder id → its DIRECT
// children. Mirrors iOS's one-screen-per-folder model, but with a single fetch
// per pathway (the lazy boundary is per-pathway, then drill in-memory).
//
// Only the four content pathways have a folders.json (business_english,
// 180_days_step_by_step, advanced_learners, skills_based_training). The other
// three flat pathways are leaf-only — `loadFolderMap` resolves to null for them.

const CONTENT_BASE = "/content";

/**
 * @typedef {object} FolderItem
 * @property {string}  id            prefixed ("item_90821" or "item_MMU_2164")
 * @property {string}  type          string type (ITEM_TYPE_MAP value, "content"
 *                                    for sub-folders, or "mmu_media" for raw
 *                                    word-media leaves with no /exercise screen)
 * @property {boolean} [isFolder]    true for `content` drilldown rows
 * @property {string}  [titleEn]
 * @property {string}  [titleZh]
 * @property {number}  [requiredPlay]
 *
 * @typedef {object} FolderEntry
 * @property {string} titleEn
 * @property {string} titleZh
 * @property {FolderItem[]} items
 *
 * @typedef {object} FolderMap
 * @property {string} pathwayId
 * @property {number} folderCount
 * @property {Record<string, FolderEntry>} folders
 */

/**
 * Per-session cache keyed by pathwayId. The value is the parsed FolderMap, or
 * `null` for a pathway with no folders.json (leaf-only / not-found) so we don't
 * re-fetch a known-absent file. The folder data is static for the visit.
 * @type {Map<string, FolderMap | null>}
 */
const _folderMapCache = new Map();

/**
 * Load (and memoise) a pathway's folder-content index. Returns null for a
 * pathway that ships no folders.json (the three leaf-only flat pathways, ZTF,
 * or any fetch/parse failure) — callers treat null as "no drilldown data".
 *
 * @param {string} pathwayId
 * @returns {Promise<FolderMap | null>}
 */
export async function loadFolderMap(pathwayId) {
  if (_folderMapCache.has(pathwayId)) {
    return /** @type {FolderMap | null} */ (_folderMapCache.get(pathwayId));
  }
  /** @type {FolderMap | null} */
  let map = null;
  try {
    const res = await fetch(`${CONTENT_BASE}/pathways/${pathwayId}/folders.json`);
    if (res.ok) {
      map = /** @type {FolderMap} */ (await res.json());
    }
  } catch {
    map = null; // absent or unreachable — leaf-only pathway
  }
  _folderMapCache.set(pathwayId, map);
  return map;
}

/**
 * Resolve a single folder's direct children within a pathway. `folderId` is the
 * bare plist id (no `item_` prefix), e.g. "90587". Returns null when the pathway
 * has no folder map or the id isn't a known folder.
 *
 * @param {string} pathwayId
 * @param {string} folderId
 * @returns {Promise<FolderEntry | null>}
 */
export async function loadFolder(pathwayId, folderId) {
  const map = await loadFolderMap(pathwayId);
  if (!map || !map.folders) return null;
  return map.folders[String(folderId)] ?? null;
}

/** Test seam — drop the cache so a fixture fetch is re-read. */
export function _resetFolderCache() {
  _folderMapCache.clear();
}
